import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Switch, FormControlLabel, Box, Stack
} from '@mui/material';
import { Shift, CreateShiftRequest } from '../../interfaces/shift/Shift';
import { ShiftService } from '../../services/shift.service';
import { useToast } from '../../context/ToastContext';

interface Props {
    open: boolean;
    onClose: () => void;
    shift?: Shift | null;
    institutionId: string;
    onSaved: () => void;
}

interface FormState {
    name: string;
    code: string;
    startTime: string;
    endTime: string;
    graceMinutesIn: string;
    graceMinutesOut: string;
    otThresholdMinutes: string;
    otMaxMinutes: string;
    isActive: boolean;
}

interface FormErrors {
    name?: string;
    code?: string;
    startTime?: string;
    endTime?: string;
    graceMinutesIn?: string;
    graceMinutesOut?: string;
    otThresholdMinutes?: string;
    otMaxMinutes?: string;
}

const CODE_REGEX = /^[A-Z0-9_]+$/;

// Convert "HH:mm:ss" → "HH:mm" for MUI time input
function toTimeInput(t: string): string {
    return t ? t.substring(0, 5) : '';
}

// Convert "HH:mm" → "HH:mm:ss" for backend
function toBackendTime(t: string): string {
    return t ? `${t}:00` : '';
}

const emptyForm = (): FormState => ({
    name: '',
    code: '',
    startTime: '',
    endTime: '',
    graceMinutesIn: '0',
    graceMinutesOut: '0',
    otThresholdMinutes: '0',
    otMaxMinutes: '',
    isActive: true,
});

const shiftToForm = (s: Shift): FormState => ({
    name: s.name,
    code: s.code,
    startTime: toTimeInput(s.startTime),
    endTime: toTimeInput(s.endTime),
    graceMinutesIn: String(s.graceMinutesIn),
    graceMinutesOut: String(s.graceMinutesOut),
    otThresholdMinutes: String(s.otThresholdMinutes),
    otMaxMinutes: s.otMaxMinutes != null ? String(s.otMaxMinutes) : '',
    isActive: s.isActive,
});

const ShiftDetailDialog: React.FC<Props> = ({ open, onClose, shift, institutionId, onSaved }) => {
    const toast = useToast();
    const [form, setForm] = useState<FormState>(emptyForm());
    const [errors, setErrors] = useState<FormErrors>({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setForm(shift ? shiftToForm(shift) : emptyForm());
            setErrors({});
        }
    }, [open, shift]);

    const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm(prev => ({ ...prev, [key]: value }));
        setErrors(prev => ({ ...prev, [key]: undefined }));
    };

    const validate = (): boolean => {
        const errs: FormErrors = {};

        if (!form.name.trim()) errs.name = 'Name is required';
        if (!form.code.trim()) {
            errs.code = 'Code is required';
        } else if (!CODE_REGEX.test(form.code)) {
            errs.code = 'Code must be uppercase letters, digits, or underscores';
        }
        if (!form.startTime) errs.startTime = 'Start time is required';
        if (!form.endTime) errs.endTime = 'End time is required';
        if (form.startTime && form.endTime && form.startTime === form.endTime) {
            errs.endTime = 'Start and end time must differ';
        }
        const numericFields: (keyof Pick<FormState, 'graceMinutesIn' | 'graceMinutesOut' | 'otThresholdMinutes'>)[] = [
            'graceMinutesIn', 'graceMinutesOut', 'otThresholdMinutes',
        ];
        // Upper limits mirror the server (CreateShiftRequest): grace 240, OT threshold 480, OT max 720.
        const maxFor = { graceMinutesIn: 240, graceMinutesOut: 240, otThresholdMinutes: 480 };
        for (const f of numericFields) {
            const val = Number(form[f]);
            if (isNaN(val) || val < 0) {
                errs[f] = 'Must be 0 or greater';
            } else if (val > maxFor[f]) {
                errs[f] = `Must be ${maxFor[f]} or less`;
            }
        }
        if (form.otMaxMinutes !== '' && (isNaN(Number(form.otMaxMinutes)) || Number(form.otMaxMinutes) < 0)) {
            errs.otMaxMinutes = 'Must be 0 or greater (leave blank for unlimited)';
        } else if (form.otMaxMinutes !== '' && Number(form.otMaxMinutes) > 720) {
            errs.otMaxMinutes = 'Must be 720 or less (leave blank for unlimited)';
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        const body: CreateShiftRequest = {
            name: form.name.trim(),
            code: form.code.trim(),
            startTime: toBackendTime(form.startTime),
            endTime: toBackendTime(form.endTime),
            graceMinutesIn: Number(form.graceMinutesIn),
            graceMinutesOut: Number(form.graceMinutesOut),
            otThresholdMinutes: Number(form.otThresholdMinutes),
            otMaxMinutes: form.otMaxMinutes !== '' ? Number(form.otMaxMinutes) : null,
            isActive: form.isActive,
        };

        setSaving(true);
        try {
            if (shift) {
                await ShiftService.update(institutionId, shift.id, body);
                toast.showSuccess('Shift updated successfully');
            } else {
                await ShiftService.create(institutionId, body);
                toast.showSuccess('Shift created successfully');
            }
            onSaved();
            onClose();
        } catch (err) {
            toast.showApiError(err, 'Failed to save shift');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ 'data-help-page': 'shifts.shift.page' } as any} maxWidth="sm" fullWidth>
            <DialogTitle>{shift ? 'Edit Shift' : 'New Shift'}</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    <Stack data-help-section="shifts.shift.section.details" spacing={2}>
                        <TextField
                            data-help="shifts.shift.field.name" label="Name"
                            fullWidth
                            value={form.name}
                            onChange={e => setField('name', e.target.value)}
                            error={!!errors.name}
                            helperText={errors.name}
                            required
                            inputProps={{ 'data-testid': 'shift-name' }}
                        />
                        <TextField
                            data-help="shifts.shift.field.code" label="Code"
                            fullWidth
                            value={form.code}
                            onChange={e => setField('code', e.target.value.toUpperCase())}
                            error={!!errors.code}
                            helperText={errors.code || 'Uppercase letters, digits, underscores'}
                            required
                            inputProps={{ 'data-testid': 'shift-code' }}
                        />
                        <Box data-help-section="shifts.shift.section.times" sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                            <TextField
                                data-help="shifts.shift.field.startTime" label="Start Time"
                                type="time"
                                fullWidth
                                value={form.startTime}
                                onChange={e => setField('startTime', e.target.value)}
                                error={!!errors.startTime}
                                helperText={errors.startTime}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ 'data-testid': 'shift-start-time' }}
                            />
                            <TextField
                                data-help="shifts.shift.field.endTime" label="End Time"
                                type="time"
                                fullWidth
                                value={form.endTime}
                                onChange={e => setField('endTime', e.target.value)}
                                error={!!errors.endTime}
                                helperText={errors.endTime}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ 'data-testid': 'shift-end-time' }}
                            />
                        </Box>
                        <Box data-help-section="shifts.shift.section.grace" sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                            <TextField
                                data-help="shifts.shift.field.graceIn" label="Grace In (minutes)"
                                type="number"
                                fullWidth
                                value={form.graceMinutesIn}
                                onChange={e => setField('graceMinutesIn', e.target.value)}
                                error={!!errors.graceMinutesIn}
                                helperText={errors.graceMinutesIn}
                                inputProps={{ min: 0, max: 240, 'data-testid': 'shift-grace-in' }}
                            />
                            <TextField
                                data-help="shifts.shift.field.graceOut" label="Grace Out (minutes)"
                                type="number"
                                fullWidth
                                value={form.graceMinutesOut}
                                onChange={e => setField('graceMinutesOut', e.target.value)}
                                error={!!errors.graceMinutesOut}
                                helperText={errors.graceMinutesOut}
                                inputProps={{ min: 0, max: 240, 'data-testid': 'shift-grace-out' }}
                            />
                        </Box>
                        <Box data-help-section="shifts.shift.section.overtime" sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                            <TextField
                                data-help="shifts.shift.field.otThreshold" label="OT Threshold (minutes)"
                                type="number"
                                fullWidth
                                value={form.otThresholdMinutes}
                                onChange={e => setField('otThresholdMinutes', e.target.value)}
                                error={!!errors.otThresholdMinutes}
                                helperText={errors.otThresholdMinutes}
                                inputProps={{ min: 0, max: 480, 'data-testid': 'shift-ot-threshold' }}
                            />
                            <TextField
                                data-help="shifts.shift.field.otMax" label="OT Max (minutes, blank = unlimited)"
                                type="number"
                                fullWidth
                                value={form.otMaxMinutes}
                                onChange={e => setField('otMaxMinutes', e.target.value)}
                                error={!!errors.otMaxMinutes}
                                helperText={errors.otMaxMinutes}
                                inputProps={{ min: 0, max: 720, 'data-testid': 'shift-ot-max' }}
                            />
                        </Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.isActive}
                                    onChange={e => setField('isActive', e.target.checked)}
                                    data-testid="shift-active"
                                />
                            }
                            data-help="shifts.shift.field.active" label="Active"
                        />
                    </Stack>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button data-help="shifts.shift.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button data-help="shifts.shift.save" onClick={handleSubmit} variant="contained" disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ShiftDetailDialog;
